import { Eye, EyeOff, LockKeyhole, RefreshCw, Settings } from "lucide-react";
import type { Owner } from "../types";
import { settingsSubviews, type View } from "./views";
import Avatar from "../components/Avatar";
import Brand from "../components/Brand";
import type { BrandEnvironment } from "../lib/branding";
import { usePrivacy } from "../components/Privacy";

// The bar across the top of every screen: brand and release on phones, privacy and version on
// wide screens, and refresh, settings and profile everywhere.

export default function Topbar({ environment, view, nav, version, appVersion, refreshing, onRefresh, profile, onOpenProfile }: {
  environment?: BrandEnvironment;
  view: View;
  nav: (view: View) => void;
  /** The release alone, for the compact phone header. */
  version: string;
  /** The release and build, for the wide header. */
  appVersion: string;
  refreshing: boolean;
  onRefresh: () => void;
  profile: Owner;
  onOpenProfile: () => void;
}) {
  const { hidden: amountsHidden, toggle: toggleAmounts } = usePrivacy();
  return (
    <header className="topbar">
      <div className="mobile-brand">
        <Brand environment={environment} />
        <small className="mobile-release" title={appVersion}>
          {version}
        </small>
      </div>
      <span className="desktop-label">YOUR HOUSEHOLD, AT A GLANCE</span>
      <div className="topbar-actions">
        <span className="private-pill">
          <LockKeyhole size={12} /> Private household
        </span>
        <span className="version-pill">{appVersion}</span>
        <button
          className={"icon-button " + (amountsHidden ? "active" : "")}
          aria-label={amountsHidden ? "Show amounts" : "Hide amounts"}
          aria-pressed={amountsHidden}
          title={amountsHidden ? "Show amounts" : "Hide amounts"}
          onClick={toggleAmounts}
        >
          {amountsHidden ? <EyeOff size={19} /> : <Eye size={19} />}
        </button>
        <button
          className={"icon-button " + (refreshing ? "refreshing" : "")}
          aria-label="Refresh data"
          title="Refresh data"
          disabled={refreshing}
          onClick={onRefresh}
        >
          <RefreshCw size={19} />
        </button>
        <button
          className={
            "icon-button " +
            (view === "settings" || settingsSubviews.includes(view)
              ? "active"
              : "")
          }
          aria-label="Settings"
          title="Settings"
          onClick={() => nav("settings")}
        >
          <Settings size={19} />
        </button>
        <button
          className="profile-button"
          onClick={onOpenProfile}
          aria-label={`Profile menu, ${profile}`}
        >
          <Avatar owner={profile} size={34} />
        </button>
      </div>
    </header>
  );
}
